// Entity registry — single source of truth for how every entity renders.
// Components read this metadata; nothing is hardcoded per page (regel 7).
import {
  CheckSquare, ClipboardList, BookOpen, AlertTriangle, ChefHat, FileText,
  Megaphone, ShieldCheck, Wrench, Thermometer,
} from "lucide-react";

// tone keys map to StatusBadge styling
export const ENTITIES = {
  task: {
    label: "Uppgift", plural: "Uppgifter", icon: CheckSquare, accent: "#4F6BED",
    statuses: {
      todo: { label: "Att göra", tone: "neutral" },
      in_progress: { label: "Pågår", tone: "info" },
      blocked: { label: "Blockerad", tone: "danger" },
      done: { label: "Klar", tone: "success" },
    },
    fields: ["title", "assignee", "due_at", "priority"],
    capabilities: { ai: true, attachments: true, comments: true, audit: true, tags: true, status: true },
    permissions: { create: "task.create", update: "task.update", delete: "task.delete" },
  },
  checklist: {
    label: "Checklista", plural: "Checklistor", icon: ClipboardList, accent: "#229954",
    statuses: {
      draft: { label: "Utkast", tone: "neutral" },
      published: { label: "Aktiv", tone: "success" },
      archived: { label: "Arkiverad", tone: "muted" },
    },
    fields: ["title", "frequency"],
    capabilities: { ai: true, attachments: false, comments: true, audit: true, tags: true, status: true },
    permissions: { create: "checklist.create", update: "checklist.create", delete: "manual.delete" },
  },
  manual: {
    label: "Manual", plural: "Manualer", icon: BookOpen, accent: "#C58E1A",
    statuses: {
      draft: { label: "Utkast", tone: "neutral" },
      published: { label: "Publicerad", tone: "success" },
      archived: { label: "Arkiverad", tone: "muted" },
    },
    fields: ["title", "category"],
    capabilities: { ai: true, attachments: true, comments: true, audit: true, tags: true, status: true },
    permissions: { create: "manual.create", update: "manual.update", delete: "manual.delete" },
  },
  incident: {
    label: "Problem", plural: "Problem", icon: AlertTriangle, accent: "#E74C3C",
    statuses: {
      open: { label: "Öppen", tone: "danger" },
      investigating: { label: "Utreds", tone: "warning" },
      resolved: { label: "Åtgärdad", tone: "info" },
      closed: { label: "Stängd", tone: "success" },
    },
    fields: ["title", "severity", "reporter"],
    capabilities: { ai: true, attachments: true, comments: true, audit: true, tags: true, status: true },
    permissions: { create: "incident.create", update: "incident.update", delete: "task.delete" },
  },
  recipe: {
    label: "Recept", plural: "Recept", icon: ChefHat, accent: "#E15B2D",
    statuses: {
      draft: { label: "Utkast", tone: "neutral" },
      published: { label: "Publicerad", tone: "success" },
      archived: { label: "Arkiverad", tone: "muted" },
    },
    fields: ["title", "allergens"],
    capabilities: { ai: true, attachments: true, comments: true, audit: true, tags: true, status: true },
    permissions: { create: "manual.create", update: "manual.update", delete: "manual.delete" },
  },
  report: {
    label: "Rapport", plural: "Rapporter", icon: FileText, accent: "#566573",
    statuses: {},
    fields: ["title", "period"],
    capabilities: { ai: true, attachments: true, comments: true, audit: true, tags: true, status: false },
    permissions: { create: "report.create", update: "report.create", delete: "task.delete" },
  },
  announcement: {
    label: "Anslag", plural: "Anslag", icon: Megaphone, accent: "#8E44AD",
    statuses: {
      published: { label: "Publicerad", tone: "success" },
      archived: { label: "Arkiverad", tone: "muted" },
    },
    fields: ["title", "audience"],
    capabilities: { ai: false, attachments: true, comments: true, audit: true, tags: true, status: true },
    permissions: { create: "manual.create", update: "manual.update", delete: "manual.delete" },
  },
  inspection: {
    label: "Inspektion", plural: "Inspektioner", icon: ShieldCheck, accent: "#2980B9",
    statuses: {
      planned: { label: "Planerad", tone: "neutral" },
      completed: { label: "Genomförd", tone: "info" },
      passed: { label: "Godkänd", tone: "success" },
      failed: { label: "Underkänd", tone: "danger" },
    },
    fields: ["title", "kind", "score"],
    capabilities: { ai: true, attachments: true, comments: true, audit: true, tags: true, status: true },
    permissions: { create: "checklist.create", update: "checklist.create", delete: "manual.delete" },
  },
  maintenance: {
    label: "Underhåll", plural: "Underhåll", icon: Wrench, accent: "#B9770E",
    statuses: {
      planned: { label: "Planerad", tone: "neutral" },
      due: { label: "Förfallen", tone: "warning" },
      done: { label: "Utförd", tone: "success" },
    },
    fields: ["title", "next_due"],
    capabilities: { ai: true, attachments: true, comments: true, audit: true, tags: true, status: true },
    permissions: { create: "task.create", update: "task.update", delete: "task.delete" },
  },
  temperature_log: {
    label: "Temperatur", plural: "Temperaturloggar", icon: Thermometer, accent: "#1ABC9C",
    statuses: {
      in_range: { label: "Inom gräns", tone: "success" },
      out_of_range: { label: "Avvikelse", tone: "danger" },
    },
    fields: ["location", "value"],
    capabilities: { ai: true, attachments: true, comments: true, audit: true, tags: false, status: true },
    permissions: { create: "checklist.complete", update: "task.update", delete: "task.delete" },
  },
};

export function getEntity(type) {
  return ENTITIES[type] || {
    label: type, plural: type, icon: FileText, accent: "#566573",
    statuses: {}, fields: ["title"],
    capabilities: { ai: false, attachments: false, comments: false, audit: false, tags: false, status: false },
    permissions: {},
  };
}

export function statusMeta(type, status) {
  const e = getEntity(type);
  return e.statuses?.[status] || { label: status, tone: "neutral" };
}
